
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUserRole } from "@/hooks/useUserRole";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, Car, Image as ImageIcon, AlertCircle, Info, Search } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { DataOneResponse } from "@/types/dataone";
import { 
  VehicleOverview,
  AdasFeatures,
  EquipmentFeatures,
  ColorsMaterials,
  VehicleImages,
  DataOneDebugView,
  AdasAnalysis,
  DataOneUtils
} from "@/components/dataone";
import { VinLookupHistory } from "@/components/vin/VinLookupHistory";
import { useVinLookup } from "@/hooks/useVinLookup";

interface DataOneApiResponse {
  status: number;
  data?: any;
  error?: string;
  details?: string;
}

const DataOneApi = () => {
  const { isAdmin, loading } = useUserRole();
  const { lookupHistory, deleteLookup } = useVinLookup();
  const navigate = useNavigate();
  const [vinNumber, setVinNumber] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [dataOneData, setDataOneData] = useState<DataOneApiResponse | null>(null);
  const [activeTab, setActiveTab] = useState("organized");
  const [error, setError] = useState<string | null>(null);
  const [dbSavedEntry, setDbSavedEntry] = useState<any | null>(null);
  const [dataValid, setDataValid] = useState<boolean>(false);
  const [debugInfo, setDebugInfo] = useState<any>(null);

  useEffect(() => {
    if (!loading && !isAdmin()) {
      toast.error("You don't have permission to access this page");
      navigate("/");
    }
  }, [loading, isAdmin, navigate]);

  useEffect(() => {
    // Validate the data when it changes
    if (dataOneData?.data) {
      try {
        // Extract basic vehicle info as a validation check
        const vehicleInfo = DataOneUtils.getVehicleInfo(dataOneData.data);
        console.log("Data validation - Vehicle info:", vehicleInfo);
        setDataValid(!!vehicleInfo);
        
        if (!vehicleInfo) {
          console.warn("DataOne response validation failed - couldn't extract vehicle info");
          setDebugInfo({
            responseStructure: Object.keys(dataOneData.data),
            hasQueryResponses: !!dataOneData.data.query_responses,
            responseFirstLevel: dataOneData.data.query_responses ? 
              Object.keys(dataOneData.data.query_responses) : 'No query_responses found',
            inspectedPaths: {
              standardPath: !!dataOneData.data.query_responses?.[Object.keys(dataOneData.data.query_responses)[0]]?.styles?.style,
              usMarketPath: !!dataOneData.data.query_responses?.[Object.keys(dataOneData.data.query_responses)[0]]?.us_market_data?.us_styles
            }
          });
        } else {
          setDebugInfo(null);
        }
      } catch (err) {
        console.error("Error validating DataOne response:", err);
        setDataValid(false);
        setDebugInfo({ error: String(err) });
      }
    } else {
      setDataValid(false);
    }
  }, [dataOneData]);

  const checkForExistingData = async (vin: string) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) {
        return null;
      }
      
      const { data, error } = await supabase
        .from("vin_lookups")
        .select("*")
        .eq("vin", vin)
        .eq("user_id", userData.user.id)
        .maybeSingle();
      
      if (error) {
        console.error("Error fetching existing VIN lookup:", error);
        return null;
      }
      
      return data;
    } catch (err) {
      console.error("Error in checkForExistingData:", err);
      return null;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setDbSavedEntry(null);
    setDebugInfo(null);

    // Validate VIN - must be exactly 17 characters
    if (!vinNumber || vinNumber.length !== 17) {
      toast.error("Please enter a valid 17-character VIN");
      return;
    }

    setIsProcessing(true);
    setDataOneData(null);
    
    try {
      // Check if we already have this VIN in our database
      const existingData = await checkForExistingData(vinNumber);
      
      if (existingData?.raw_dataone_response) {
        // Use the saved response from the database
        console.log("Found cached DataOne response for VIN:", vinNumber);
        setDataOneData({
          status: 200,
          data: existingData.raw_dataone_response
        });
        setDbSavedEntry(existingData);
        toast.info("Retrieved saved DataOne data from database");
        setIsProcessing(false);
        return;
      }
      
      // Get current user
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) {
        throw new Error("User not authenticated");
      }
      
      // Call the Supabase Edge Function
      console.log("Calling dataone-vin-lookup edge function with VIN:", vinNumber);
      
      const { data, error } = await supabase.functions.invoke('dataone-vin-lookup', {
        body: JSON.stringify({ 
          vin: vinNumber,
          userId: userData.user.id 
        }),
      });
      
      console.log("Edge function response:", { data, error });
      
      if (error) {
        console.error("Edge function error:", error);
        throw new Error(error.message || "Failed to lookup VIN");
      }

      if (!data) {
        toast.error("Empty response from DataOne API");
        setError("Empty response received from the VIN lookup service");
        setIsProcessing(false);
        return;
      }

      setDataOneData(data);
      toast.success("VIN lookup successful!");
      
      // Fetch the updated entry from the database
      setTimeout(async () => {
        const updatedEntry = await checkForExistingData(vinNumber);
        if (updatedEntry) {
          setDbSavedEntry(updatedEntry);
        }
      }, 1500); // Give the database a moment to update
      
    } catch (error: any) {
      console.error("Error processing request:", error);
      
      let errorMessage = "Failed to process VIN lookup";
      if (error.message?.includes("404")) {
        errorMessage = "Edge function not found. Please make sure it's deployed correctly.";
      } else if (error.message?.includes("403")) {
        errorMessage = "Not authorized to access the edge function. Please check your authentication.";
      } else if (error.message?.includes("415")) {
        errorMessage = "Content type error: API requires form-urlencoded format.";
      }
      
      toast.error(errorMessage);
      setError(errorMessage);
      setDataOneData({ error: error?.message || String(error), status: 500 });
    }
    
    setIsProcessing(false);
  };

  const handleTabChange = (value: string) => {
    setActiveTab(value);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAdmin()) {
    return null;
  }

  // Convert lookupHistory to the format expected by VinLookupHistory
  const formattedLookupHistory = lookupHistory.map(history => ({
    ...history,
    raw_dataone_response: history.raw_dataone_response || null
  }));

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">DataOne VIN Lookup</h1>
      
      <Alert className="bg-amber-50 border-amber-200">
        <AlertTitle>DataOne API Integration</AlertTitle>
        <AlertDescription>
          This page connects to the DataOne VIN Decoder API with enhanced ADAS feature detection.
          Enter a valid 17-character VIN to retrieve comprehensive vehicle data.
          {dbSavedEntry && (
            <div className="mt-2 text-sm">
              <span className="font-semibold">Database status:</span> Response saved to database
            </div>
          )}
        </AlertDescription>
      </Alert>
      
      {error && (
        <Alert variant="destructive" className="bg-red-50 border-red-200">
          <AlertTitle>Error</AlertTitle>
          <AlertDescription className="space-y-2">
            <p>{error}</p>
          </AlertDescription>
        </Alert>
      )}
      
      <Card className="p-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="vin" className="block text-sm font-medium">
              Enter VIN Number
            </label>
            
            <div className="flex gap-2">
              <Input
                id="vin"
                value={vinNumber}
                onChange={(e) => setVinNumber(e.target.value.toUpperCase())}
                placeholder="Enter 17-character VIN"
                maxLength={17}
                className="flex-1"
              />
              <Button
                type="submit"
                disabled={isProcessing || vinNumber.length !== 17}
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Processing
                  </>
                ) : (
                  "Submit"
                )}
              </Button>
            </div>
          </div>
        </form>
      </Card>

      {dataOneData && (
        <Tabs
          value={activeTab}
          onValueChange={handleTabChange}
          className="w-full max-w-full"
        >
          <TabsList className="mb-2 w-full max-w-xl">
            <TabsTrigger value="organized">Summary View</TabsTrigger>
            <TabsTrigger value="raw">Raw JSON</TabsTrigger>
            {!dataValid && <TabsTrigger value="debug">Debug Info</TabsTrigger>}
          </TabsList>
          
          {/* Summary View Tab - Original Organized View */}
          <TabsContent value="organized" className="w-full">
            <Card className="p-0 w-full max-w-full">
              <ScrollArea className="h-[70vh] w-full">
                <div className="px-6 py-4">
                  {dataOneData?.error ? (
                    <div className="text-red-600">{dataOneData.error}</div>
                  ) : !dataValid ? (
                    <Alert variant="destructive" className="mb-4">
                      <AlertCircle className="h-4 w-4" />
                      <AlertTitle>Data Structure Issue</AlertTitle>
                      <AlertDescription>
                        We encountered an issue processing the DataOne response structure. 
                        Please check the Raw JSON or Debug tabs for more information.
                      </AlertDescription>
                    </Alert>
                  ) : (
                    <div className="space-y-8">
                      {/* Pass the entire data object to each component */}
                      <VehicleOverview data={dataOneData.data} />
                      
                      {/* ADAS Features */}
                      <AdasFeatures data={dataOneData.data} />
                      
                      {/* Equipment & Features */}
                      <EquipmentFeatures data={dataOneData.data} />
                      
                      {/* Colors & Materials */}
                      <ColorsMaterials data={dataOneData.data} />
                      
                      {/* Vehicle Images */}
                      <VehicleImages data={dataOneData.data} />
                    </div>
                  )}
                </div>
              </ScrollArea>
            </Card>
          </TabsContent>
          
          {/* Raw JSON View Tab */}
          <TabsContent value="raw" className="w-full">
            <Card className="p-0 w-full max-w-full">
              <ScrollArea className="h-[70vh] w-full">
                <div className="px-6 py-4">
                  <div className="mb-4 flex justify-end">
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => {
                        navigator.clipboard.writeText(JSON.stringify(dataOneData, null, 2));
                        toast.success('JSON copied to clipboard');
                      }}
                    >
                      Copy JSON
                    </Button>
                  </div>
                  <Textarea
                    readOnly
                    value={JSON.stringify(dataOneData, null, 2)}
                    className="w-full text-xs font-mono bg-gray-50 border-none min-h-[300px] resize-none"
                    style={{
                      minWidth: 0,
                      width: "100%",
                      whiteSpace: "pre-wrap",
                    }}
                  />
                </div>
              </ScrollArea>
            </Card>
          </TabsContent>

          {/* Debug Info Tab - Only shown when data validation fails */}
          {!dataValid && (
            <TabsContent value="debug" className="w-full">
              <DataOneDebugView data={dataOneData.data} debugInfo={debugInfo} />
            </TabsContent>
          )}
        </Tabs>
      )}

      {/* VIN Lookup History Section */}
      <div className="mt-10">
        <h2 className="text-2xl font-bold mb-4">VIN Lookup History</h2>
        <VinLookupHistory lookups={formattedLookupHistory} onDelete={deleteLookup} />
      </div>
    </div>
  );
};

export default DataOneApi;
